import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useOfferStore } from '@/lib/store';
import { databaseService } from '@/lib/database-service';
import {
  filterEntriesForPartialExport,
  type PartialExportFilter,
} from '@shared/gaeb-partial';
import { normalizePositionNumber } from '@shared/gaeb-price-match';
import { Loader2, Scissors } from 'lucide-react';

/**
 * X83-Splitter: exportiert einen Positionsbereich (Von/Bis-Positionsnummer)
 * und/oder ausgewählte Gewerke des geladenen LV als eigenständige X83,
 * z.B. zum Versand an Subunternehmer oder Großhandel.
 *
 * Übergeordnete Titel-/Header-Einträge der gewählten Positionen werden
 * automatisch mitgenommen, damit die BoQ-Hierarchie erhalten bleibt.
 *
 * Eigenständige Button+Dialog-Komponente. Seit Welle 1.3 der UX-Entlastung
 * wahlweise als CONTROLLED Geschwister-Dialog im "Export ▾"-Menü der
 * Angebots-Sidebar (Props `open`/`onOpenChange`); die eigenständige Einbindung
 * im GAEB-Editor (GAEBEditor.tsx) bleibt unverändert (ohne `open` → eigener
 * Trigger + interner Open-State).
 */
interface GaebTeilExportProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function GaebTeilExport({ open: openProp, onOpenChange: onOpenChangeProp }: GaebTeilExportProps = {}) {
  const isGAEBDatabase = useOfferStore((s) => s.isGAEBDatabase);
  const gaebMetadata = useOfferStore((s) => s.gaebMetadata);
  const currentDatabaseId = useOfferStore((s) => s.currentDatabaseId);
  const positions = useOfferStore((s) => s.positions);
  const gewerkOrder = useOfferStore((s) => s.gewerkOrder);
  const { toast } = useToast();

  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const isOpen = isControlled ? openProp : internalOpen;
  const setIsOpen = (open: boolean) => {
    if (isControlled) onOpenChangeProp?.(open);
    else setInternalOpen(open);
  };
  const [isExporting, setIsExporting] = useState(false);
  const [selectedGewerke, setSelectedGewerke] = useState<string[]>([]);
  const [vonPos, setVonPos] = useState('');
  const [bisPos, setBisPos] = useState('');

  // Verfügbare Gewerke: gepflegte Reihenfolge, ergänzt um alle aus den Positionen
  const availableGewerke = useMemo(() => {
    const fromPositions = Array.from(
      new Set(
        positions
          .map((p) => p.gewerk?.trim())
          .filter((g): g is string => Boolean(g)),
      ),
    );
    const ordered = gewerkOrder.filter((g) => fromPositions.includes(g));
    const rest = fromPositions.filter((g) => !ordered.includes(g)).sort((a, b) => a.localeCompare(b, 'de'));
    return [...ordered, ...rest];
  }, [positions, gewerkOrder]);

  const filter: PartialExportFilter = useMemo(
    () => ({
      gewerke: selectedGewerke.length > 0 ? selectedGewerke : undefined,
      vonPos: vonPos.trim() || undefined,
      bisPos: bisPos.trim() || undefined,
    }),
    [selectedGewerke, vonPos, bisPos],
  );

  const hasFilter = Boolean(filter.gewerke || filter.vonPos || filter.bisPos);

  // Vorschau: Anzahl gewählter Positionen (auf Basis der geladenen Positionen)
  const selectedCount = useMemo(() => {
    if (!hasFilter) return 0;
    const filtered = filterEntriesForPartialExport(
      positions.map((p) => ({
        positionNumber: (p as any).positionNumber ?? null,
        type: (p as any).type ?? 'item',
        gewerk: p.gewerk ?? null,
      })),
      filter,
    );
    return filtered.filter((entry) => entry.type !== 'title').length;
  }, [positions, filter, hasFilter]);

  // Nur im GAEB-Modus sinnvoll (konsistent zu GAEBExport)
  if (!isGAEBDatabase || !gaebMetadata || !currentDatabaseId) {
    return null;
  }

  const toggleGewerk = (gewerk: string, checked: boolean) => {
    setSelectedGewerke((prev) =>
      checked ? [...prev, gewerk] : prev.filter((g) => g !== gewerk),
    );
  };

  const buildFilename = (): string => {
    const von = normalizePositionNumber(vonPos) || 'Anfang';
    const bis = normalizePositionNumber(bisPos) || 'Ende';
    if (vonPos.trim() || bisPos.trim()) {
      return `Teil-LV_${von}-${bis}.x83`.replace(/[^\w.\-]+/g, '_');
    }
    if (selectedGewerke.length > 0) {
      const gewerkPart = selectedGewerke.join('_').substring(0, 40);
      return `Teil-LV_${gewerkPart}.x83`.replace(/[^\w.\-]+/g, '_');
    }
    return 'Teil-LV.x83';
  };

  const handleExport = async () => {
    if (!hasFilter || selectedCount === 0) return;
    setIsExporting(true);
    try {
      // Rohdaten der GAEB-Datenbank laden (gleiche Quelle wie loadDatabaseData im Store),
      // damit alle GAEB-Felder (gaebOriginalId, Texte, Hierarchie) im Export erhalten bleiben.
      const database = await databaseService.loadDatabase(currentDatabaseId);
      const allEntries: any[] = Array.isArray(database.entries) ? database.entries : [];

      const filteredEntries = filterEntriesForPartialExport(allEntries, filter);
      const itemCount = filteredEntries.filter((entry: any) => entry.type !== 'title').length;

      if (itemCount === 0) {
        toast({
          title: 'Keine Positionen im Bereich',
          description: 'Die Auswahl enthält keine exportierbaren Positionen.',
          variant: 'destructive',
        });
        return;
      }

      const filename = buildFilename();
      const response = await fetch('/api/gaeb/export-partial', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entries: filteredEntries,
          metadata: database.gaebMetadata || gaebMetadata,
          exportFormat: 'x83',
          filename,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Export fehlgeschlagen' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Teil-LV exportiert',
        description: `${itemCount} Position(en) in ${filename}`,
      });
      setIsOpen(false);
    } catch (error) {
      toast({
        title: 'Teil-Export fehlgeschlagen',
        description: error instanceof Error ? error.message : 'Unbekannter Fehler',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {/* Eigener Trigger nur im ungesteuerten Standalone-Modus (GAEBEditor).
          Im Menü (controlled) sitzt der Trigger als DropdownMenuItem im Parent. */}
      {!isControlled && (
        <DialogTrigger asChild>
          <Button variant="outline" className="flex items-center space-x-2 border-blue-200 text-blue-700 hover:bg-blue-50">
            <Scissors className="h-4 w-4" />
            <span>Teil-LV exportieren</span>
          </Button>
        </DialogTrigger>
      )}

      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Scissors className="h-5 w-5 text-blue-600" />
            <span>Teil-LV als X83 exportieren</span>
          </DialogTitle>
          <DialogDescription>
            Positionsbereich oder Gewerke auswählen und als eigenständige X83-Datei exportieren
            (z.B. für Subunternehmer oder Großhandel).
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-1">
          {/* Gewerk-Auswahl */}
          {availableGewerke.length > 0 && (
            <div className="space-y-2">
              <Label>Gewerke</Label>
              <div className="max-h-40 overflow-y-auto space-y-1 border rounded-md p-2">
                {availableGewerke.map((gewerk) => (
                  <div key={gewerk} className="flex items-center space-x-2">
                    <Checkbox
                      id={`teil-export-gewerk-${gewerk}`}
                      checked={selectedGewerke.includes(gewerk)}
                      onCheckedChange={(checked) => toggleGewerk(gewerk, checked === true)}
                    />
                    <Label htmlFor={`teil-export-gewerk-${gewerk}`} className="font-normal cursor-pointer text-sm">
                      {gewerk}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500">Keine Auswahl = alle Gewerke.</p>
            </div>
          )}

          {/* Von/Bis Positionsnummer */}
          <div className="space-y-2">
            <Label>Positionsbereich (optional)</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="teil-export-von" className="text-xs text-gray-500">Von Pos-Nr</Label>
                <Input
                  id="teil-export-von"
                  value={vonPos}
                  onChange={(e) => setVonPos(e.target.value)}
                  placeholder="z.B. 1.1"
                />
              </div>
              <div>
                <Label htmlFor="teil-export-bis" className="text-xs text-gray-500">Bis Pos-Nr</Label>
                <Input
                  id="teil-export-bis"
                  value={bisPos}
                  onChange={(e) => setBisPos(e.target.value)}
                  placeholder="z.B. 1.500"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500">
              Vergleich über normalisierte Positionsnummern (führende Nullen werden ignoriert).
            </p>
          </div>

          {/* Auswahl-Zusammenfassung */}
          <div className="flex items-center gap-2">
            <Badge variant={selectedCount > 0 ? 'default' : 'secondary'} className={selectedCount > 0 ? 'bg-blue-600' : ''}>
              {selectedCount} Position(en) gewählt
            </Badge>
            {!hasFilter && (
              <span className="text-xs text-gray-500">Bitte Gewerke oder einen Positionsbereich wählen.</span>
            )}
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 gap-2">
          <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isExporting}>
            Abbrechen
          </Button>
          <Button
            onClick={handleExport}
            disabled={!hasFilter || selectedCount === 0 || isExporting}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isExporting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Exportiere...</>
            ) : (
              <>X83 exportieren</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
